import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from "react-router-dom";

import MenuBar from './../components/Menubar';
import User from './../components/Utils';
import CategoryItems from '../components/CategoryItems';
import LoaderContext from './../components/LoaderContext';
import LoginPage from './LoginPage';
import { categoriesArray } from '../utils/helpers';
import UserPageAvatar from '../components/UserPageAvatar';
import { useCravings } from '../hooks/useCravings';

type Props = {
	user: User,
	handleLogin(user): void
};

const loadData = () => {

}

const UserPage: React.FC<Props> = (props) => {
	const [error, setError] = useState('');
	const [userName, setUserName] = useState("");
	const [items, setItems] = useState<any[]>([]);
	const [showLogin, setShowLogin] = useState(false);
  const [showJumpToTopButton, setShowJumpToTopButton] = useState(false);
  const [isFollowed, setIsFollowed] = useState<boolean | null>(null);
	const [profileUrl, setProfileUrl] = useState<string | null>(null);
	const { id }: any = useParams();
	const { showLoader, hideLoader } = useContext(LoaderContext);
	const { cravedIds, handleToggleCraving } = useCravings(props.user);
	let didCancel = false;

	const baseStyle = {
		display: "inline-block",
		padding: "6px 24px",
		borderRadius: "50px",
		margin: "6px 0 10px 0",
		fontSize: "2rem",
		fontWeight: 500,
		textDecoration: "none",
		transition: "background 0.2s,color 0.2s",
		border: "none",
		outline: "none",
		cursor: "pointer",
	};

	const followStyle = {
		...baseStyle,
		color: "#000",
		backgroundColor: "#7bf1d2",
	};

	const unfollowStyle = {
		...baseStyle,
		color: "#001",
		backgroundColor: "#ff884d",
	};

	useEffect(() => {
		const fetchFollowState = async () => {
			try {
				const response = await fetch(process.env.REACT_APP_SERVER_URL + `/follow/is-followed?userId=${id}`, {
					method: 'GET',
					headers:  new Headers({
						'Content-Type': 'application/json',
						"Accept":"application/json",
						'Authorization': 'Bearer ' + props.user.token
					}),
				});
				const data = await response.json();
				setIsFollowed(data.message === "Yes");
			} catch (e) {
				setIsFollowed(false);
			}
		};
		fetchFollowState();
	}, [props.user.id, props.user.token]);

	const log = async (message: any) => {
		if (process.env.REACT_APP_LOG === 'true') {
			console.log(message);
		}
	}

	const handleErrors = (response) => {
		log(response);
		/*
		ok: false
		​redirected: false
		​status: 403
		​statusText: ""
		​type: "cors"
		*/
		if (!response.ok) {
			var err = response.statusText;
			if (err === '') {
				err = 'Error';
			}
			throw Error(err);
		}
		return response;
	}

	const loadData = async () => {
		showLoader();
		var headers = new Headers({
				'Content-Type': 'application/json',
				"Accept":"application/json",
			}
		);
		if (props.user.isLogged) {
			headers.append('Authorization', 'Bearer ' + props.user.token);
		}
		fetch(process.env.REACT_APP_SERVER_URL + "/users/" + id + "?v=" + Date.now(), {
			method: 'GET',
			headers: headers
		})
			.then(handleErrors)
			.then(res => res.json())
			.then(
				(result) => {
					log(result);
					if (!didCancel) {
						hideLoader();
						setUserName(result.userName);
						setItems(result.items);
						setProfileUrl(result.profileUrl ?? null);
						setError("");
					}
				},
				(error) => {
					log(error);
					if (!didCancel) {
						hideLoader();
						setError(error.message);
					}
				}
			)
	};

	useEffect(() => {
		loadData();

		return () => {
			didCancel = true;
		};
	}, [props.user.isLogged]);

	const handleRemoveUpvote = async (itemId: number) => {
		showLoader();
		try {
			await fetch(
				`${process.env.REACT_APP_SERVER_URL}/items/${itemId}/removeUpvote`,
				{
					method: 'DELETE',
					headers: {
						Authorization: 'Bearer ' + props.user.token,
					},
				}
			);

			setItems(prev =>
				prev.map(i =>
					i.id === itemId
						? { ...i, upvoted: false, canUpvote: true, upvoteCount: Math.max(0, i.upvoteCount - 1) }
						: i
				)
			);
		} finally {
			hideLoader();
		}
	};

	const handleUpvote = async (itemId: number) => {
		if (!props.user.isLogged) {
			handleLogin();
			return;
		}

		showLoader();
		try {
			await fetch(
				`${process.env.REACT_APP_SERVER_URL}/items/${itemId}/upvote`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: 'Bearer ' + props.user.token,
					},
					body: JSON.stringify({ userId: props.user.id }),
				}
			);

			setItems(prev =>
				prev.map(i =>
					i.id === itemId
						? { ...i, upvoted: true, canUpvote: false, upvoteCount: i.upvoteCount + 1 }
						: i
				)
			);
		} finally {
			hideLoader();
		}
    	};



	useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle scroll for Jump to Top button
  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const scrollPosition = window.scrollY;
    const scrollThreshold = windowHeight * 1;
    setShowJumpToTopButton(scrollPosition > scrollThreshold);
  };

  const handleJumpToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

	const handleFollowToggle = async () => {
		try {
			const url = process.env.REACT_APP_SERVER_URL +
				(isFollowed
					? `/follow/un-follow-user?userId=${id}`
					: `/follow/follow-user?userId=${id}`);

			const method = isFollowed ? 'DELETE' : 'POST';

			const response = await fetch(url, {
				method: method,
				headers: new Headers({
					'Accept': 'application/json',
					'Authorization': 'Bearer ' + props.user.token,
					...(method === 'POST' && {'Content-Type': 'application/json'}),
				}),
			});

			if (response.status === 401 || response.status === 403) {
				if (!props.user.isLogged) {
					window.location.href = '/login';
					return;
				}
			}

			if (!response.ok) {
				throw new Error(`Error ${response.status}`);
			}

			const data = await response.json();
			setIsFollowed(prev => !prev);
		} catch (error) {
			alert("Failed to update follow state");
		}
	};

	const handleLogin = async () => {
		setShowLogin(true);
	}

	const onLoginBackClick = async () => {
		setShowLogin(false);
	}

	const getCategoryItems = (category) => {
		let list: any[] = [];
		for (var i = 0; i < items.length; i++) {
			if (items[i].categoryId === category) {
				list.push(items[i]);
			}
		}
		return list;
	}

	if (!props.user.isLogged) {
    return (
      <main className="main-bg">
        <div
          className="container mypage"
          style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
          <MenuBar user={props.user} />
          <section
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              className="page-box-main text-center"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 18,
              }}
            >
             <h1 className="font-reklame" style={{ margin: 0 }}>
                 You must be signed in to view this information
             </h1>
             <Link className="tap-to-upvote-btn upvote_btn pill-std" to="/login">
                 Log in
             </Link>
                   <a
                 className="tap-to-upvote-btn upvote_btn pill-std"
                 style={{ cursor: 'pointer' }}
                 onClick={() => window.history.back()}
             >
                 Go back
       </a>
            </div>
          </section>
        </div>
        {showJumpToTopButton && (
          <button className="jump-to-top-button" onClick={handleJumpToTop}>
            Jump to Top
          </button>
        )}
      </main>
    );
  }

	if (showLogin && !props.user.isLogged) {
		return (
			<LoginPage user={props.user} handleLogin={props.handleLogin} showBack={true} showHome={false} backgroundImage={true} handleBack={onLoginBackClick}/>
		)
	} else {
		return (
      <main>
        {userName !== '' && (
          <div className="container mypage">
            <MenuBar user={props.user} />
            <section>
              <div className="mypage-title userpage-title-layout">
                <h1 className="trend-sans"> {userName} PAGE</h1>
								<UserPageAvatar profileUrl={profileUrl} />
							</div>

							{String(props.user.id) !== String(id) && (
								<div style={{ display: "flex", justifyContent: "center", marginTop:'20px', marginBottom: "5px" }}>
									<button
										className="user-follow-btn"
										style={isFollowed ? unfollowStyle : followStyle}
										onClick={handleFollowToggle}
									>
										{isFollowed ? "UNFOLLOW" : "FOLLOW"}
									</button>
								</div>
							)}

							<div className="page-box-main">
                {error !== '' && <p>{error}</p>}
                <div>
                  {categoriesArray.map((category) => (
                    <CategoryItems
                      key={category.id}
                      items={getCategoryItems(category.id)}
                      name={category.name}
                      user={props.user}
                      editable={false}
                      handleLogin={handleLogin}
                      handleUpvote={handleUpvote}
											handleRemoveUpvote={handleRemoveUpvote}
                      loadData={loadData}
                       cravedIds={cravedIds}
                    handleToggleCraving={handleToggleCraving}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}
        {userName === '' && error !== '' && (
          <div className="container mypage">
            <section>
              <div className="page-box-main text-center">
                <p>{error}</p>
              </div>
            </section>
          </div>
        )}
        {showJumpToTopButton && (
          <button className="jump-to-top-button" onClick={handleJumpToTop}>
            Jump to Top
          </button>
        )}
      </main>
    );
	}
}

export default UserPage;